import { Grid2, MenuItem, TextField } from "@mui/material";
import { TextFieldStyles } from "../../../styles/commonStyles";
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { toolsValidationSchema } from "../../../validation/schemas/toolsSchema";
import { PLACEHOLDERS } from "../../../constants/placeholders";

const InfoToolForm = ({
  toolTypes,
  sensorTypes,
  frequencyUpdateRanges,
  onToolChange,
  onFormDataChange,
  control,
  errors,
}) => {
  const [formData, setFormData] = useState({
    tool: "",
    sensorType: "",
    devEui: "",
    appEui: "",
    frequencyUpdate: "",
    appKey: "",
    gatewayName: "",
    gatewayEui: "",
  });

  const isSensor = formData.tool === "sensor";
  const isGateway = formData.tool === "gateway";

  const handleInputChange = useCallback(
    (field) => (e) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    },
    []
  );

  useEffect(() => {
    if (onToolChange) {
      onToolChange(formData.tool);
    }

    let preparedFormData = {};
    if (isSensor) {
      preparedFormData = {
        devEUI: formData.devEui,
        appEUI: formData.appEui,
        appKey: formData.appKey,
        update_frequency: formData.frequencyUpdate,
        sensor_type: formData.sensorType,
      };
    } else if (isGateway) {
      preparedFormData = {
        nickname: formData.gatewayName,
        devEUI: formData.gatewayEui,
      };
    }

    if (onFormDataChange) {
      onFormDataChange(preparedFormData);
    }
  }, [formData, onToolChange, onFormDataChange, isSensor, isGateway]);

  useEffect(() => {
    if (formData.tool) {
      setFormData((prev) => ({
        tool: prev.tool,
        sensorType: "",
        devEui: "",
        appEui: "",
        frequencyUpdate: "",
        appKey: "",
        gatewayName: "",
        gatewayEui: "",
      }));
    }
  }, [formData.tool]);

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          name="tool"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="filled"
              fullWidth
              id="filled-select-tool"
              label="Strumento"
              select
              sx={{
                ...TextFieldStyles,
              }}
              value={formData.tool || ""}
              onChange={(e) => {
                field.onChange(e);
                handleInputChange("tool")(e);
              }}
            >
              {toolTypes && toolTypes.length > 0 ? (
                toolTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={""}>Nessuno strumento disponibile</MenuItem>
              )}
            </TextField>
          )}
        />
      </Grid2>
      {isSensor && (
        <>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="sensorType"
              control={control}
              rules={toolsValidationSchema.sensorType}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  fullWidth
                  id="type-input"
                  sx={{ ...TextFieldStyles }}
                  label="Tipologia"
                  select
                  value={formData.sensorType || ""}
                  error={!!errors.sensorType}
                  helperText={errors.sensorType?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("sensorType")(e);
                  }}
                >
                  {sensorTypes && sensorTypes.length > 0 ? (
                    sensorTypes.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={""}>
                      Nessuna tipologia disponibile
                    </MenuItem>
                  )}
                </TextField>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="devEui"
              control={control}
              rules={toolsValidationSchema.devEui}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  fullWidth
                  id="dev-eui-input"
                  sx={{ ...TextFieldStyles }}
                  label="Dev EUI"
                  value={formData.devEui || ""}
                  error={!!errors.devEui}
                  helperText={errors.devEui?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("devEui")(e);
                  }}
                  placeholder={PLACEHOLDERS.TOOLS.DEV_EUI}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="appEui"
              control={control}
              rules={toolsValidationSchema.appEui}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  fullWidth
                  id="app-eui-input"
                  sx={{ ...TextFieldStyles }}
                  label="App EUI"
                  value={formData.appEui || ""}
                  error={!!errors.appEui}
                  helperText={errors.appEui?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("appEui")(e);
                  }}
                  placeholder={PLACEHOLDERS.TOOLS.APP_EUI}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="appKey"
              control={control}
              rules={toolsValidationSchema.appKey}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  fullWidth
                  id="app-key-input"
                  sx={{ ...TextFieldStyles }}
                  label="App Key"
                  value={formData.appKey || ""}
                  error={!!errors.appKey}
                  helperText={errors.appKey?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("appKey")(e);
                  }}
                  placeholder={PLACEHOLDERS.TOOLS.APP_KEY}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="frequencyUpdate"
              control={control}
              rules={toolsValidationSchema.frequencyUpdate}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="filled"
                  fullWidth
                  id="frequency-update-input"
                  sx={{ ...TextFieldStyles }}
                  label="Frequenza aggiornamento"
                  value={formData.frequencyUpdate || ""}
                  error={!!errors.frequencyUpdate}
                  helperText={errors.frequencyUpdate?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("frequencyUpdate")(e);
                  }}
                  select
                >
                  {frequencyUpdateRanges && frequencyUpdateRanges.length > 0 ? (
                    frequencyUpdateRanges.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {`${option.minute} minuti`}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={""}>
                      Nessun range di tempo disponibile
                    </MenuItem>
                  )}
                </TextField>
              )}
            />
          </Grid2>
        </>
      )}
      {isGateway && (
        <>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="gatewayName"
              control={control}
              rules={toolsValidationSchema.gatewayName}
              render={({ field }) => (
                <TextField
                  variant="filled"
                  fullWidth
                  id="gatewayName"
                  sx={{ ...TextFieldStyles }}
                  label="Nome"
                  value={formData.gatewayName || ""}
                  error={!!errors.gatewayName}
                  helperText={errors.gatewayName?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("gatewayName")(e);
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
              name="gatewayEui"
              control={control}
              rules={toolsValidationSchema.gatewayEui}
              render={({ field }) => (
                <TextField
                  variant="filled"
                  fullWidth
                  id="gatewayEui"
                  sx={{ ...TextFieldStyles }}
                  label="EUI"
                  value={formData.gatewayEui || ""}
                  error={!!errors.gatewayEui}
                  helperText={errors.gatewayEui?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange("gatewayEui")(e);
                  }}
                  placeholder={PLACEHOLDERS.TOOLS.GATEWAY_EUI}
                />
              )}
            />
          </Grid2>
        </>
      )}
    </Grid2>
  );
};

export default InfoToolForm;
